import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { PUMP_MODES, deleteFlowSchedule, saveFlowSchedule, type FlowSchedule, type PumpMode, type ScheduleSegment } from '../../services/flowSurveyService';
import '../lighting/Lighting.css';
import './FlowMap.css';

interface Row {
  readonly key: number;
  start: string;
  end: string;
  mode: PumpMode;
  min: string;
  max: string;
}

const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;
let nextKey = 1;
const toRow = (s: ScheduleSegment): Row => ({ key: nextKey++, start: s.start, end: s.end, mode: s.mode, min: String(s.minPercent), max: String(s.maxPercent) });
/** A new aquarium starts with the usual day / night split. */
const DEFAULT_ROWS = (): Row[] => [
  { key: nextKey++, start: '08:00', end: '20:00', mode: 'random', min: '40', max: '100' },
  { key: nextKey++, start: '20:00', end: '08:00', mode: 'night', min: '20', max: '50' },
];

export interface PumpScheduleEditorProps {
  aquariumId: string;
  /** The pump's name — the same the flow surveys use ("Brand Model"). */
  pumpLabel: string;
  schedule: FlowSchedule | null;
  onChanged: () => void;
}

/**
 * The day schedule of one pump: segments over 24 h (from, until, mode, lowest
 * and highest output). Entered once per aquarium; every flow survey uses it to
 * estimate the flow at other times of day. A segment may run past midnight.
 */
export function PumpScheduleEditor({ aquariumId, pumpLabel, schedule, onChanged }: PumpScheduleEditorProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>(() => (schedule ? schedule.segments.map(toRow) : DEFAULT_ROWS()));
  const [errors, setErrors] = useState<Record<string, string>>({});
  // A pump without a programme shows one line and a button, not an empty six-field form:
  // the page opened with four empty editors (usability audit §4.3).
  const [editing, setEditing] = useState(schedule !== null);
  const idBase = `schedule-${pumpLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const save = useMutation({
    mutationFn: (segments: ScheduleSegment[]) => saveFlowSchedule(aquariumId, pumpLabel, segments),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => deleteFlowSchedule(aquariumId, schedule!.id),
    onSuccess: onChanged,
  });

  const update = (key: number, patch: Partial<Row>): void => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const segments: ScheduleSegment[] = [];
    for (const r of rows) {
      const min = Number(r.min);
      const max = Number(r.max);
      if (!CLOCK.test(r.start)) next[`${r.key}-start`] = String(t('flow.schedule.timeInvalid'));
      if (!CLOCK.test(r.end)) next[`${r.key}-end`] = String(t('flow.schedule.timeInvalid'));
      if (r.min.trim() === '' || !Number.isInteger(min) || min < 0 || min > 100) next[`${r.key}-min`] = String(t('flow.editor.percentInvalid'));
      if (r.max.trim() === '' || !Number.isInteger(max) || max < 1 || max > 100) next[`${r.key}-max`] = String(t('flow.editor.percentInvalid'));
      if (Number.isInteger(min) && Number.isInteger(max) && min > max) next[`${r.key}-min`] = String(t('flow.editor.rangeOrder'));
      segments.push({ start: r.start, end: r.end, mode: r.mode, minPercent: min, maxPercent: max });
    }
    setErrors(next);
    if (Object.keys(next).length === 0) save.mutate(segments);
  };

  if (!editing) {
    return (
      <Card>
        <div data-testid={`${idBase}`}>
          <Stack gap="3">
            <Heading level={3}>{pumpLabel}</Heading>
            <Text size="sm" color="secondary">{t('flow.schedule.notConfigured')}</Text>
            <Inline gap="2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                {t('flow.schedule.create')}
              </Button>
            </Inline>
          </Stack>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={submit} noValidate data-testid={`${idBase}`}>
        <Stack gap="4">
          <Heading level={3}>{pumpLabel}</Heading>
          {rows.length === 0 && <Text size="sm" color="secondary">{t('flow.schedule.noSegments')}</Text>}
          <ol className="lighting__list">
            {rows.map((r, i) => {
              const n = i + 1;
              return (
                <li key={r.key} className="lighting__row" data-testid="schedule-segment">
                  <Inline gap="3" align="start">
                    <Input id={`${idBase}-${r.key}-start`} type="time" label={t('flow.schedule.from', { n })} value={r.start} onChange={(e) => update(r.key, { start: e.target.value })} error={errors[`${r.key}-start`]} />
                    <Input id={`${idBase}-${r.key}-end`} type="time" label={t('flow.schedule.until', { n })} value={r.end} onChange={(e) => update(r.key, { end: e.target.value })} error={errors[`${r.key}-end`]} />
                    <Select
                      id={`${idBase}-${r.key}-mode`}
                      label={t('flow.schedule.mode', { n })}
                      value={r.mode}
                      options={PUMP_MODES.map((m) => ({ value: m, label: String(t(`flow.modes.${m}`)) }))}
                      onChange={(e) => update(r.key, { mode: e.target.value as PumpMode })}
                    />
                    <Input id={`${idBase}-${r.key}-min`} type="number" inputMode="numeric" min={0} max={100} label={t('flow.schedule.min', { n })} value={r.min} onChange={(e) => update(r.key, { min: e.target.value })} error={errors[`${r.key}-min`]} />
                    <Input id={`${idBase}-${r.key}-max`} type="number" inputMode="numeric" min={1} max={100} label={t('flow.schedule.max', { n })} value={r.max} onChange={(e) => update(r.key, { max: e.target.value })} error={errors[`${r.key}-max`]} />
                  </Inline>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} aria-label={t('flow.schedule.removeSegment', { n })}>
                    {t('flow.remove')}
                  </Button>
                </li>
              );
            })}
          </ol>
          {save.isError && <Alert compact>{t('flow.schedule.error')}</Alert>}
          {remove.isError && <Alert compact>{t('flow.schedule.deleteError')}</Alert>}
          {save.isSuccess && <Alert tone="success" live="polite" compact>{t('flow.schedule.saved')}</Alert>}
          <Inline gap="2" wrap>
            <Button
              type="button"
              variant="ghost"
              disabled={rows.length >= 12}
              onClick={() => setRows((rs) => [...rs, { key: nextKey++, start: rs.at(-1)?.end ?? '08:00', end: rs[0]?.start ?? '20:00', mode: 'constant', min: '50', max: '50' }])}
            >
              {t('flow.schedule.addSegment')}
            </Button>
            <Button type="submit" variant="secondary" isLoading={save.isPending}>{t('flow.schedule.save')}</Button>
            {schedule && (
              <Button type="button" variant="ghost" isLoading={remove.isPending} onClick={() => remove.mutate()}>
                {t('flow.schedule.delete')}
              </Button>
            )}
          </Inline>
        </Stack>
      </form>
    </Card>
  );
}

export default PumpScheduleEditor;
